import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PORTFOLIO_ITEMS } from "@/lib/constants";
import { ArrowUpRight, Instagram } from "lucide-react";
import { BRAND } from "@/lib/constants";
import { getCaseByPortfolioId } from "@/data/seo/caseStudies";

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-20 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 md:gap-8 mb-10 md:mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <span className="w-8 h-px bg-[var(--iw-gold)]" />
              <span className="overline text-[var(--iw-gold)]">
                Selected Works
              </span>
            </div>
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-[40px] sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Work that goes
              <br />
              <span className="italic gold-rose-gradient">straight to stories</span>.
            </h2>
          </div>
          <p className="text-[var(--iw-muted)] max-w-sm text-sm md:text-base leading-relaxed">
            Every invite below was built one-to-one on WhatsApp — real couples,
            real families, real reels that went viral in their circle.
          </p>
        </div>

        {/* Uniform responsive grid — works cleanly on mobile, tablet and desktop */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {PORTFOLIO_ITEMS.map((it, idx) => (
            <motion.article
              key={it.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.8,
                delay: (idx % 4) * 0.06,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="relative group overflow-hidden border border-white/10 hover:border-[var(--iw-gold)]/50 transition-colors aspect-[4/5]"
              data-testid={`portfolio-item-${it.id}`}
            >
              <img
                src={it.img}
                alt={`${it.title} — ${it.category} | Premium digital wedding invitation by InvitationWala`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.08]"
              />
              {/* Shine sweep */}
              <span
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                style={{
                  background:
                    "linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.08) 45%, rgba(232,165,180,0.15) 52%, rgba(255,255,255,0.08) 58%, transparent 80%)",
                }}
              />
              {/* Bottom gradient for legibility */}
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(10,10,10,0) 45%, rgba(10,10,10,0.55) 72%, rgba(10,10,10,0.95) 100%)",
                }}
              />
              <div className="absolute inset-0 p-4 md:p-5 flex flex-col justify-end">
                <span
                  className="overline mb-2 text-[9px] md:text-[10px]"
                  style={{ color: "var(--iw-gold)" }}
                >
                  {it.category}
                </span>
                <div className="flex items-end justify-between gap-3">
                  <h3
                    className="font-serif-display text-[var(--iw-ivory)] text-xl md:text-2xl leading-[1.1] tracking-tight"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {it.title}
                  </h3>
                  <span className="hidden md:grid w-9 h-9 border border-[var(--iw-gold)] place-items-center text-[var(--iw-gold)] shrink-0 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    <ArrowUpRight size={14} />
                  </span>
                </div>
              </div>
              {getCaseByPortfolioId(it.id) && (
                <Link
                  to={`/work/${getCaseByPortfolioId(it.id).slug}`}
                  className="absolute inset-0 z-10"
                  aria-label={`Read the ${it.title} case study`}
                  data-testid={`portfolio-case-link-${it.id}`}
                />
              )}
            </motion.article>
          ))}
        </div>

        {/* Inline CTA */}
        <div className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <a
            href={BRAND.instagramUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost-gold text-xs md:text-sm"
            data-testid="portfolio-ig-cta"
          >
            <Instagram size={14} />
            See every reel on Instagram
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
